import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { getCountries, getFilteredCountries } from '../../../../../redux/actions/countries'
import { getLanguages } from '../../../../../redux/actions/languages'
import { createNovelty, toggleFormNovelty } from '../../../../../redux/actions/novelties'
import { getRoles } from '../../../../../redux/actions/roles'
import { formValidator, resetFormValidator } from '../../../../../redux/actions/validator'
import { IRootState } from '../../../../../redux/types'
import { INovelty } from '../../../novelties/models/novelties'
import { IStatus, IVisibility } from '@/contexts/backoffice/courses/models/course'

export const useNewsModal = () => {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()

  const { newsSelected } = useSelector((state: IRootState) => state.novelty)
  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { languages } = useSelector((state: IRootState) => state.language)
  const { roles } = useSelector((state: IRootState) => state.role)
  const { isValidForm } = useSelector((state: IRootState) => state.validator)

  const STATUS: IStatus[] = [
    { id: 0, name: t('active') },
    { id: 1, name: t('inactive') },
    { id: 2, name: t('draft') }
  ]

  const VISIBILITY: IVisibility[] = [
    { id: 0, name: t('private') },
    { id: 1, name: 'VIP' },
    { id: 2, name: t('public') }
  ]

  const [noveltyState, setNoveltyState] = useState<Partial<INovelty>>({
    id: null,
    brand: null,
    countries: [],
    languages: [],
    creator: '',
    title: '',
    content: '',
    fileName: [],
    type: 2,
    status: STATUS[0],
    role: null,
    highlight: 0,
    visibility: VISIBILITY[0]
  })

  useEffect(() => {
    if (roles.length === 0) {
      dispatch(getRoles())
    }
    if (languages.length === 0) {
      dispatch(getLanguages())
    }
  }, [])

  useEffect(() => {
    if (newsSelected != null) {
      setNoveltyState({
        id: newsSelected.id,
        brand: newsSelected.brand,
        countries: newsSelected.countriesIds,
        languages: newsSelected.languagesIds,
        creator: newsSelected.creator.name,
        title: newsSelected.title,
        content: newsSelected.content,
        fileName: [],
        type: newsSelected.type,
        status: STATUS[newsSelected.status],
        role: newsSelected.role,
        highlight: newsSelected.highlight,
        visibility: VISIBILITY[newsSelected.visibility]
      })
    }
  }, [newsSelected])

  useEffect(() => {
    if (authUser != null) {
      if (authUser.role_id === 1) dispatch(getCountries())
      else dispatch(getFilteredCountries())
    }
  }, [authUser])

  useEffect(() => {
    if (isValidForm) {
      dispatch(createNovelty({ ...noveltyState, typeNovelty: 'newsSelected' }))
      handleCloseNewsModal()
    }
  }, [isValidForm])

  const handleInputChange = (inputName: string, value: any) => {
    setNoveltyState({ ...noveltyState, [inputName]: value })
  }

  const getUploadParams = ({ meta }: any) => {
    let tmpFileNames = noveltyState.fileName ? [...noveltyState.fileName] : []
    tmpFileNames.push(meta.name)
    setNoveltyState({ ...noveltyState, fileName: tmpFileNames })
    return {
      url: `${import.meta.env.VITE_API_URL}/novelties/uploaddropzone/${meta.name}`
    }
  }

  const handleCloseNewsModal = () => {
    dispatch(resetFormValidator())
    dispatch(toggleFormNovelty({ type: 'newsSelected', change: false }))
  }

  const handleSubmit = () => {
    if (noveltyState.id == null) {
      dispatch(
        formValidator({
          countries: [noveltyState.countries, 'notempty', 'general'],
          languages: [noveltyState.languages, 'notempty', 'general'],
          title: [noveltyState.title, 'required|max:250|min:2'],
          content: [noveltyState.content, 'notempty', 'general'],
          fileName: [noveltyState.fileName, 'notempty', 'addOn'],
          role: [noveltyState.role, 'required', 'general']
        })
      )
    } else {
      dispatch(
        formValidator({
          id: [noveltyState.id, 'required'],
          countries: [noveltyState.countries, 'notempty', 'general'],
          languages: [noveltyState.languages, 'notempty', 'general'],
          title: [noveltyState.title, 'required|max:250|min:2'],
          content: [noveltyState.content, 'notempty', 'general'],
          role: [noveltyState.role, 'required', 'general']
        })
      )
    }
  }

  return {
    STATUS,
    VISIBILITY,
    noveltyState,
    handleInputChange,
    handleCloseNewsModal,
    handleSubmit,
    getUploadParams
  }
}
